import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import {
  InterventionRequise,
  MonitoringError,
  CyberDashboardStats,
  RecentActivity,
  AgdrefPending,
} from '../../types';

interface CyberDashboardState {
  interventions: InterventionRequise[];
  interventionsTotal: number;
  monitoringErrors: MonitoringError[];
  stats: CyberDashboardStats | null;
  recentActivity: RecentActivity[];
  agdrefPending: AgdrefPending[];
  loading: boolean;
  error: string | null;
}

const initialState: CyberDashboardState = {
  interventions: [],
  interventionsTotal: 0,
  monitoringErrors: [],
  stats: null,
  recentActivity: [],
  agdrefPending: [],
  loading: false,
  error: null,
};

// Thunks asynchrones

/**
 * Récupérer les interventions requises
 */
export const fetchInterventions = createAsyncThunk(
  'cyberDashboard/fetchInterventions',
  async ({ limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/cyber-dashboard/interventions?limit=${limit}&offset=${offset}`
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des interventions');
    }
  }
);

/**
 * Récupérer les erreurs de monitoring
 */
export const fetchMonitoringErrors = createAsyncThunk(
  'cyberDashboard/fetchMonitoringErrors',
  async ({ days = 7, limit = 50 }: { days?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/cyber-dashboard/monitoring-errors?days=${days}&limit=${limit}`
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des erreurs');
    }
  }
);

/**
 * Récupérer les statistiques
 */
export const fetchStats = createAsyncThunk(
  'cyberDashboard/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/cyber-dashboard/stats`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des statistiques');
    }
  }
);

/**
 * Récupérer l'activité récente
 */
export const fetchRecentActivity = createAsyncThunk(
  'cyberDashboard/fetchRecentActivity',
  async ({ limit = 20 }: { limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/cyber-dashboard/recent-activity?limit=${limit}`
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération de l\'activité');
    }
  }
);

/**
 * Récupérer les comptes en attente d'AGDREF
 */
export const fetchAgdrefPending = createAsyncThunk(
  'cyberDashboard/fetchAgdrefPending',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/cyber-dashboard/agdref-pending`);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erreur lors de la récupération des comptes AGDREF');
    }
  }
);

// Slice Redux
const cyberDashboardSlice = createSlice({
  name: 'cyberDashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDashboard: (state) => {
      state.interventions = [];
      state.interventionsTotal = 0;
      state.monitoringErrors = [];
      state.stats = null;
      state.recentActivity = [];
      state.agdrefPending = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch interventions
    builder.addCase(fetchInterventions.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchInterventions.fulfilled, (state, action) => {
      state.loading = false;
      state.interventions = action.payload.demarches;
      state.interventionsTotal = action.payload.total;
    });
    builder.addCase(fetchInterventions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch monitoring errors
    builder.addCase(fetchMonitoringErrors.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMonitoringErrors.fulfilled, (state, action) => {
      state.loading = false;
      state.monitoringErrors = action.payload;
    });
    builder.addCase(fetchMonitoringErrors.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch stats
    builder.addCase(fetchStats.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchStats.fulfilled, (state, action) => {
      state.loading = false;
      state.stats = action.payload;
    });
    builder.addCase(fetchStats.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch recent activity
    builder.addCase(fetchRecentActivity.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchRecentActivity.fulfilled, (state, action) => {
      state.loading = false;
      state.recentActivity = action.payload;
    });
    builder.addCase(fetchRecentActivity.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch AGDREF pending
    builder.addCase(fetchAgdrefPending.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAgdrefPending.fulfilled, (state, action) => {
      state.loading = false;
      state.agdrefPending = action.payload;
    });
    builder.addCase(fetchAgdrefPending.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, clearDashboard } = cyberDashboardSlice.actions;
export default cyberDashboardSlice.reducer;
